import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Header from '../../components/customer/Header';
import Footer from '../../components/customer/Footer';
import Loader from '../../components/common/Loader';
import { getReceiptUrl } from '../../utils/imageUrl';
import { useTranslation } from 'react-i18next';
import './MyOrders.css';

const MyOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchMyOrders();
  }, []);

  const fetchMyOrders = async () => {
    try {
      const response = await api.get('/orders/my-orders');
      setOrders(response.data.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'pending_verification': return '#2196f3';
      case 'confirmed': return '#4caf50';
      case 'processing': return '#2196f3';
      case 'shipped': return '#9c27b0';
      case 'delivered': return '#4caf50';
      case 'cancelled': return '#f44336';
      default: return '#999';
    }
  };

  const getStatusText = (paymentStatus, orderStatus) => {
    if (paymentStatus === 'pending') return t('orders.pending');
    if (paymentStatus === 'pending_verification') return 'ველოდებით დადასტურებას';
    if (paymentStatus === 'confirmed') {
      if (orderStatus === 'processing') return t('orders.confirmed');
      if (orderStatus === 'shipped') return 'Shipped';
      if (orderStatus === 'delivered') return 'Delivered';
    }
    return orderStatus;
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (loading) return <Loader fullscreen />;

  return (
    <>
      <Header />
      <div className="my-orders-page" style={{paddingTop: '7rem'}}>
        <div className="my-orders-container">
          <h1 className="page-title">{t('orders.title')}</h1>
          
          {orders.length === 0 ? (
            <div className="no-orders">
              <div className="no-orders-icon">📦</div>
              <h2>{t('orders.noOrders')}</h2>
              <p>{t('orders.noOrdersDescription')}</p>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => (
                <div key={order.id} className="order-card">
                  <div className="order-header" onClick={() => toggleOrderDetails(order.id)} style={{ cursor: 'pointer' }}>
                    <div className="order-id-wrapper">
                      <span className="order-id">{t('orders.orderId')} #{order.id}</span>
                      <span className="expand-icon">{expandedOrder === order.id ? '▼' : '▶'}</span>
                    </div>
                    <div 
                      className="order-status"
                      style={{ 
                        background: getStatusColor(order.payment_status),
                        color: '#fff',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      {getStatusText(order.payment_status, order.status)}
                    </div>
                  </div>
                  
                  <div className="order-details">
                    <div className="order-detail">
                      <span className="label">{t('orders.date')}:</span>
                      <span>{new Date(order.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="order-detail">
                      <span className="label">{t('orders.total')}:</span>
                      <span className="amount">{parseFloat(order.total).toFixed(2)} ₾</span>
                    </div>
                    <div className="order-detail">
                      <span className="label">{t('orders.paymentMethod')}:</span>
                      <span>{order.payment_method === 'bog_ipay' ? '💳 BOG iPay' : '🏦 Bank Transfer'}</span>
                    </div>
                    {order.receipt_url && (
                      <div className="order-detail">
                        <span className="label">{t('orders.receipt')}:</span>
                        <button
                          className="view-receipt-link"
                          onClick={() => setSelectedReceipt(`https://kid-o.ge${order.receipt_url}`)}
                        >
                          📸 ქვითარი
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Expanded Order Items */}
                  {expandedOrder === order.id && order.items && order.items.length > 0 && (
                    <div className="order-items-expanded">
                      <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
                        📦 შეკვეთილი პროდუქტები ({order.items.length})
                      </h3>
                      <div className="order-items-grid">
                        {order.items.map((item, index) => (
                          <div key={index} className="order-item-customer">
                            <div className="order-item-img">
                              {item.image_url ? (
                                <img 
                                  src={`https://kid-o.ge${item.image_url}`} 
                                  alt={item.product_name}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23f0f0f0" width="80" height="80"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3E📦%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                              ) : (
                                <div className="no-img">📦</div>
                              )}
                            </div>
                            <div className="order-item-info-customer">
                              <div className="item-name">{item.product_name}</div>
                              <div className="item-details">
                                <span>რაოდენობა: {item.quantity}</span>
                                <span>ფასი: {parseFloat(item.price).toFixed(2)} ₾</span>
                              </div>
                              <div className="item-total">
                                სულ: {(parseFloat(item.price) * parseInt(item.quantity)).toFixed(2)} ₾
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {order.payment_status === 'pending_verification' && (
                    <div className="order-notice">
                      ⏳ ველოდებით თქვენი გადახდის გადამოწმებას.
                    </div>
                  )}

                  {order.payment_status === 'confirmed' && (
                    <div className="order-notice success">
                      ✅ გადახდა დადასტურებულია!
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Receipt Modal */}
        {selectedReceipt && (
          <div className="receipt-modal" onClick={() => setSelectedReceipt(null)}>
            <div className="receipt-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedReceipt(null)}>×</button>
              <img src={selectedReceipt} alt="Receipt" />
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default MyOrders;